/**
 * Test rigs and a tiny forward-kinematics poser (bind orientation = identity, creatures §B1; pivots = creatures §B2
 * canon at neutral sliders). Writes `ActorFrame` palettes exactly as architecture §3.6 defines them:
 * palette_i = rootWorld · boneModel_i · translate(−pivot_i). For the engine's labs only — P2 owns the real runtime.
 */
import type { ActorFrame, BoneDef, BoneName, Rig } from '../../shared';
import { FEET_PER_ACTOR, TRAIL_SAMPLES, m4FromRTS, m4Mul, m4Translation, qFromEulerYXZ } from '../../shared';
import type { V3 } from './builder';

type BoneRow = [BoneName, BoneName | null, V3];

/** creatures §B2 core skeleton (P0–P34) + a 4-bone tail and one cloth bone. */
const BIPED_ROWS: BoneRow[] = [
  ['pelvis', null, [0, 0.36, 0]], ['spine_01', 'pelvis', [0, 0.44, 0]], ['spine_02', 'spine_01', [0, 0.55, 0]],
  ['belly', 'spine_01', [0, 0.45, 0.06]], ['neck', 'spine_02', [0, 0.64, 0.005]], ['head', 'neck', [0, 0.69, 0.015]],
  ['jaw', 'head', [0, 0.735, 0.06]], ['mouth', 'head', [0, 0.745, 0.125]], ['nose', 'head', [0, 0.8, 0.135]],
  ['eye_L', 'head', [0.051, 0.839, 0.105]], ['eye_R', 'head', [-0.051, 0.839, 0.105]],
  ['lid_L', 'head', [0.051, 0.839, 0.105]], ['lid_R', 'head', [-0.051, 0.839, 0.105]],
  ['brow_L', 'head', [0.055, 0.885, 0.108]], ['brow_R', 'head', [-0.055, 0.885, 0.108]],
  ['ear_L_01', 'head', [0.101, 0.955, -0.01]], ['ear_L_02', 'ear_L_01', [0.11, 1.03, -0.015]],
  ['ear_R_01', 'head', [-0.101, 0.955, -0.01]], ['ear_R_02', 'ear_R_01', [-0.11, 1.03, -0.015]],
  ['clavicle_L', 'spine_02', [0.02, 0.625, 0]], ['upperarm_L', 'clavicle_L', [0.115, 0.6, 0]],
  ['forearm_L', 'upperarm_L', [0.125, 0.44, 0]], ['hand_L', 'forearm_L', [0.13, 0.27, 0.012]],
  ['clavicle_R', 'spine_02', [-0.02, 0.625, 0]], ['upperarm_R', 'clavicle_R', [-0.115, 0.6, 0]],
  ['forearm_R', 'upperarm_R', [-0.125, 0.44, 0]], ['hand_R', 'forearm_R', [-0.13, 0.27, 0.012]],
  ['thigh_L', 'pelvis', [0.065, 0.33, 0]], ['calf_L', 'thigh_L', [0.068, 0.18, 0.012]],
  ['foot_L', 'calf_L', [0.07, 0.045, -0.01]], ['toe_L', 'foot_L', [0.07, 0.012, 0.075]],
  ['thigh_R', 'pelvis', [-0.065, 0.33, 0]], ['calf_R', 'thigh_R', [-0.068, 0.18, 0.012]],
  ['foot_R', 'calf_R', [-0.07, 0.045, -0.01]], ['toe_R', 'foot_R', [-0.07, 0.012, 0.075]],
  ['tail_1', 'pelvis', [0, 0.35, -0.07]], ['tail_2', 'tail_1', [0, 0.31, -0.22]], ['tail_3', 'tail_2', [0, 0.44, -0.32]],
  ['tail_4', 'tail_3', [0, 0.66, -0.28]], ['cloth_0', 'spine_02', [0, 0.6, -0.1]],
];

/** Snail rig (creatures §B9 'snail' family): slug body, shell, eye stalks on the ear bones. */
const SNAIL_ROWS: BoneRow[] = [
  ['pelvis', null, [0, 0.08, -0.06]], ['spine_01', 'pelvis', [0, 0.1, 0.1]], ['spine_02', 'spine_01', [0, 0.22, 0.27]],
  ['neck', 'spine_02', [0, 0.36, 0.32]], ['head', 'neck', [0, 0.47, 0.335]],
  ['ear_L_01', 'head', [0.032, 0.57, 0.335]], ['ear_L_02', 'ear_L_01', [0.055, 0.7, 0.37]],
  ['ear_R_01', 'head', [-0.032, 0.57, 0.335]], ['ear_R_02', 'ear_R_01', [-0.055, 0.7, 0.37]],
  ['shell', 'spine_01', [0, 0.28, -0.08]], ['tail_1', 'pelvis', [0, 0.05, -0.25]], ['tail_2', 'tail_1', [0, 0.03, -0.38]],
  ['hand_R', 'spine_02', [-0.1, 0.3, 0.3]], ['hand_L', 'spine_02', [0.12, 0.25, 0.32]],
];

function rigFrom(rows: BoneRow[], kind: Rig['kind']): Rig {
  const index: Partial<Record<BoneName, number>> = {};
  rows.forEach(([n], i) => { index[n] = i; });
  const bones: BoneDef[] = rows.map(([name, parent, pivot]) => ({ name, parent: parent === null ? -1 : index[parent]!, pivot: [...pivot] }));
  return {
    kind, bones, index,
    sockets: {
      prop_R: { bone: index.hand_R ?? 0, offset: [kind === 'snail' ? 0 : -0.002, kind === 'snail' ? 0 : -0.035, 0.008], rot: [0, 0, 0] },
      prop_L: { bone: index.hand_L ?? 0, offset: [0.002, -0.035, 0.008], rot: [0, 0, 0] },
    },
    rigidChains: [],
  };
}

export const bipedRig = (): Rig => rigFrom(BIPED_ROWS, 'biped');
export const snailRig = (): Rig => rigFrom(SNAIL_ROWS, 'snail');

/** Euler YXZ degrees per bone (creatures §B1 sign cheat-sheet), optional translation offset and squash. */
export type PoseSpec = Partial<Record<BoneName, [number, number, number] | { r?: [number, number, number]; t?: V3; s?: number }>>;
export interface RootSpec { x: number; z: number; yawDeg: number; mirror?: boolean; scale?: number; y?: number }

/** Allocates an `ActorFrame` for a rig (exact-size arrays, architecture pitfall 14). */
export function newActorFrame(rig: Rig): ActorFrame {
  const n = rig.bones.length;
  return {
    boneCount: n, palette: new Float32Array(n * 16), paletteLag: new Float32Array(n * 16), smearK: 0,
    contacts: new Float32Array(FEET_PER_ACTOR * 4), face: new Float32Array([1, 1, 0, 0]),
    trail: new Float32Array(TRAIL_SAMPLES * 8), trailCount: 0, animMs: 0,
  };
}

const q = new Float32Array(4), t3 = new Float32Array(3), s3 = new Float32Array(3);
const local = new Float32Array(16), tmp = new Float32Array(16), inv = new Float32Array(16);

/** FK poser. `model` holds boneModel_i; `world` = rootWorld · model_i. */
export class Poser {
  readonly model: Float32Array;
  readonly world: Float32Array;
  readonly root = new Float32Array(16);
  constructor(readonly rig: Rig) {
    this.model = new Float32Array(rig.bones.length * 16);
    this.world = new Float32Array(rig.bones.length * 16);
  }

  /** Evaluates the pose into `frame.palette` (and `frame.paletteLag` = the same, no smear). */
  pose(spec: PoseSpec, root: RootSpec, frame: ActorFrame): void {
    const R = this.root;
    const sc = root.scale ?? 1;
    qFromEulerYXZ(q, 0, root.yawDeg, 0);
    t3[0] = root.x; t3[1] = root.y ?? 0; t3[2] = root.z;
    s3[0] = root.mirror ? -sc : sc; s3[1] = sc; s3[2] = sc;
    // T · R · S with the mirror applied in model space (architecture §5.8: T · R_y(−70°) · S(−1, 1, 1))
    m4FromRTS(R, q, t3, s3);
    const bones = this.rig.bones;
    for (let i = 0; i < bones.length; i++) {
      const b = bones[i];
      const ps = spec[b.name];
      const r = Array.isArray(ps) ? ps : ps?.r;
      const off = !Array.isArray(ps) && ps?.t ? ps.t : null;
      const sq = !Array.isArray(ps) && ps?.s ? ps.s : 1;
      qFromEulerYXZ(q, r ? r[0] : 0, r ? r[1] : 0, r ? r[2] : 0);
      const pp = b.parent >= 0 ? bones[b.parent].pivot : [0, 0, 0];
      t3[0] = b.pivot[0] - pp[0] + (off ? off[0] : 0);
      t3[1] = b.pivot[1] - pp[1] + (off ? off[1] : 0);
      t3[2] = b.pivot[2] - pp[2] + (off ? off[2] : 0);
      s3[0] = 1 / Math.sqrt(sq); s3[1] = sq; s3[2] = 1 / Math.sqrt(sq);
      m4FromRTS(local, q, t3, s3);
      const out = this.model.subarray(i * 16, i * 16 + 16);
      if (b.parent >= 0) m4Mul(out, this.model.subarray(b.parent * 16, b.parent * 16 + 16), local);
      else out.set(local);
      const w = this.world.subarray(i * 16, i * 16 + 16);
      m4Mul(w, R, out);
      m4Translation(inv, -b.pivot[0], -b.pivot[1], -b.pivot[2]);
      m4Mul(tmp, w, inv);
      frame.palette.set(tmp, i * 16);
    }
    frame.paletteLag.set(frame.palette);
  }

  /** World matrix of a bone (rootWorld · boneModel); its origin is the bone's world position. */
  boneWorld(i: number): Float32Array { return this.world.subarray(i * 16, i * 16 + 16); }

  /** World position of the bone origin. */
  boneOrigin(i: number): V3 { const w = this.boneWorld(i); return [w[12], w[13], w[14]]; }

  /** World matrix of a socket (bone world · T(offset) · R(rot) · optional extra). */
  socket(name: 'prop_R' | 'prop_L', out: Float32Array, rotDeg: [number, number, number] = [0, 0, 0]): Float32Array {
    const s = this.rig.sockets[name]!;
    const bw = this.boneWorld(s.bone);
    qFromEulerYXZ(q, rotDeg[0], rotDeg[1], rotDeg[2]);
    t3[0] = s.offset[0]; t3[1] = s.offset[1]; t3[2] = s.offset[2];
    s3[0] = 1; s3[1] = 1; s3[2] = 1;
    m4FromRTS(local, q, t3, s3);
    return m4Mul(out, bw, local);
  }
}
